import "./ProductCard.css";
import { Heart, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import { useCart } from "../context/CartContext";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { isMember, isAuthenticated, setShowLoginModal } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { updateCart, getProductQuantity } = useCart();

  const mrp = Number(product.mrp || product.cost_price || 0);
  const price = Number(product.price || product.selling_price || 0);
  const basePoints = Number(product.points);
  const memberPoints = Number(product.member_points ?? product.memberPoints);
  const displayPoints =
    isMember
      ? (Number.isFinite(memberPoints) && memberPoints > 0 ? memberPoints : 0)
      : basePoints;
  const maxUnit = Number(product.max_unit ?? 0);
  const currentQty = getProductQuantity(product.id);
  const isInCart = currentQty > 0;
  const isSoldOut = maxUnit === 0;
  const isMaxReached = maxUnit > 0 && currentQty >= maxUnit;
  const showPointsBadge =
    Number.isFinite(displayPoints) && displayPoints > 0;

  const brandLabel = product.brand || product.category_name || "";
  const variantLabel = product.variant || "";
  const discount =
    mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const productPath = `/category/${encodeURIComponent(
    product.category_name || brandLabel
  )}/product/${product.id}`;

  const handleOpenProduct = () => {
    navigate(productPath);
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (isSoldOut) return;

    updateCart({
      type: "product",
      id: product.id,
      delta: 1,
    });
  };

  return (
    <div className="search-product-card">
      <div className="search-product-image-wrap">
        <button
          className={`search-product-fav ${isFavorite(product.id) ? "active" : ""}`}
          onClick={() => toggleFavorite(product.id)}
          type="button"
        >
          <Heart
            size={20}
            fill={isFavorite(product.id) ? "currentColor" : "none"}
          />
        </button>

        {showPointsBadge && (
          <div className="search-product-points-badge">₹{displayPoints} Rewards</div>
        )}

        <img
          className="search-product-image"
          src={product.image}
          alt={product.name}
          onClick={handleOpenProduct}
        />
      </div>

      <div className="search-product-info">
        <h3 className="search-product-name" onClick={handleOpenProduct}>
          {product.name}
        </h3>

        <div className="search-product-brand">
          {brandLabel} {variantLabel}
        </div>

        {isMember && mrp > price ? (
          <>
            <div className="search-product-mrp-row">
              <span className="search-product-mrp">₹{mrp}</span>
              {discount >= 10 && (
                <span className="search-product-discount">{discount}% OFF</span>
              )}
            </div>
            <div className="search-product-price-row">
              <span className="search-product-price">₹{price}</span>
            </div>
          </>
        ) : (
          <div className="search-product-price-row">
            <span className="search-product-price">₹{price}</span>
          </div>
        )}

        <div className="search-product-actions">
          {isSoldOut && (
            <button className="search-product-cart-btn" disabled type="button">
              SOLD OUT
            </button>
          )}

          {!isSoldOut && !isInCart && (
            <button
              className="search-product-cart-btn"
              onClick={handleAddToCart}
              type="button"
            >
              <ShoppingCart size={16} />
              Add To Cart
            </button>
          )}

          {!isSoldOut && isInCart && (
            <div className="search-quantity-selector">
              <button
                type="button"
                onClick={() =>
                  updateCart({
                    type: "product",
                    id: product.id,
                    delta: -1,
                  })
                }
              >
                -
              </button>
              <span>{currentQty}</span>
              <button
                type="button"
                onClick={() =>
                  updateCart({
                    type: "product",
                    id: product.id,
                    delta: 1,
                  })
                }
                disabled={isMaxReached}
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
